import {
  getCountByStatus,
  useDeleteMultipleSample,
  useGetExemplairesByMultipleprint,
} from "@/api/multiplesample";
import { CheckboxGroupMask } from "@/components/form-items/CheckboxGroupMask";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import DuplicateExemplaireDialog from "@/components/multiprint/exemplaires/DuplicateExemplaireDialog";
import ExemplaireSheetForm from "@/components/multiprint/exemplaires/ExemplaireSheetForm";
import NewExemplaireDialog from "@/components/multiprint/exemplaires/NewExemplaireDialog";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import {
  ACTIONS_COLUMN_ID,
  createActionsColumnDef,
} from "@/components/table/table-actions-column";
import { DataTable } from "@/components/ui/datatable";
import {
  exemplairesAdvancedSearchConfig,
  exemplairesColumns,
} from "@/config/dashboard/exemplaires";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Multiplesample } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "exemplaire-table-preferences";

const flatVisibleExemplaireColumns = [
  ...exemplairesColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const ExemplaireDashboard = ({
  multipleprintId,
}: {
  multipleprintId: string;
}) => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Multiplesample>({
    resourceName: "multiplesample",
    statusField: "exemplaireDansEstampesMultiplesDescriptionStatus",
    resourceCounter: (where) => getCountByStatus(multipleprintId, where),
  });

  const isFirstChild = useMemo(
    () =>
      ((countByStatus?.published as number) ?? 0) +
        ((countByStatus?.draft as number) ?? 0) ===
      0,
    [countByStatus],
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedExemplaireId, setSelectedExemplaireId] = useState<
    string | undefined
  >();
  const [initialExemplaireData, setInitialExemplaireData] = useState<
    Partial<Multiplesample> | undefined
  >();
  const [duplicateSourceId, setDuplicateSourceId] = useState<
    string | undefined
  >();

  const handleOpenExemplaireSheet = (exemplaire: {
    id: string;
    exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement?: string;
  }) => {
    setSelectedExemplaireId(exemplaire.id);
    setInitialExemplaireData({
      exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
        exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
    });
    setSheetOpen(true);
  };

  const { mutateAsync: deleteMultipleSample } = useDeleteMultipleSample();
  const handleDeleteMultipleSample = useCallback(
    async (id: string) => {
      try {
        await deleteMultipleSample(id);
        toast({
          title: t("successDeleteExemplaire"),
          variant: "success",
        });
      } catch (error) {
        toast({
          title: t("errorDeleteExemplaire"),
          variant: "destructive",
        });
        console.error(error);
      }
    },
    [deleteMultipleSample, t],
  );

  const defaultOrder = useMemo(
    () => [
      {
        exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
          "asc" as const,
      },
    ],
    [],
  );

  const orderWithDefault = useMemo(
    () =>
      fetchParams.order && fetchParams.order.length > 0
        ? fetchParams.order
        : defaultOrder,
    [fetchParams.order, defaultOrder],
  );

  const { data } = useGetExemplairesByMultipleprint({
    multipleprintId,
    where: fetchParams.where,
    order: orderWithDefault,
    take: fetchParams.take,
    skip: fetchParams.skip,
  });

  const columns: ColumnDef<Multiplesample, unknown>[] = useMemo(() => {
    const baseColumns = generateTable(exemplairesColumns, "multiplesample");
    return [
      ...baseColumns,
      createActionsColumnDef<Multiplesample>(({ row }) => {
        const exemplaire = row.original;
        const actions: TableRowAction[] = [
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            onSelect: () => {
              setSelectedExemplaireId(exemplaire.id);
              setInitialExemplaireData(undefined);
              setSheetOpen(true);
            },
          },
          {
            id: "duplicate",
            label: t(ACTION_I18N_KEYS.duplicate),
            icon: Copy,
            onSelect: () => setDuplicateSourceId(exemplaire.id),
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            onSelect: () => handleDeleteMultipleSample(exemplaire.id),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ];
  }, [t, handleDeleteMultipleSample]);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      try {
        const { order } = saved;
        return order || flatVisibleExemplaireColumns;
      } catch {
        return flatVisibleExemplaireColumns;
      }
    }
    return flatVisibleExemplaireColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        try {
          const { visibility } = saved;
          return (
            visibility || {
              ...exemplairesColumns.reduce(
                (acc, column) => ({
                  ...acc,
                  [column.key]: column.visible || false,
                }),
                {},
              ),
            }
          );
        } catch {
          // fallthrough to default
        }
      }
      return {
        ...exemplairesColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  return (
    <CheckboxGroupMask
      groupName="multipleprintExemplaireDashboardTableGroup"
      label={t("exemplaires")}
      className="w-full"
    >
    <div className="flex flex-col items-start gap-3 w-full">
      <div className="flex justify-end items-center w-full">
        <GlobalSearch
          config={exemplairesAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus ?? { published: 0, draft: 0 }}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSort config={exemplairesAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("addExemplaire")}
            Dialog={(props) => (
              <NewExemplaireDialog
                {...props}
                multipleprintId={multipleprintId}
                isFirstChild={isFirstChild}
                onCreateExemplaire={handleOpenExemplaireSheet}
              />
            )}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => {
            setSelectedExemplaireId(row.original.id);
            setInitialExemplaireData(undefined);
            setSheetOpen(true);
          }}
          pagination={{
            pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
        />
      </div>
      <DuplicateExemplaireDialog
        open={!!duplicateSourceId}
        onOpenChange={(open) => !open && setDuplicateSourceId(undefined)}
        sourceExemplaireId={duplicateSourceId}
        onDuplicateSuccess={handleOpenExemplaireSheet}
      />
      {sheetOpen && (
        <ExemplaireSheetForm
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) {
              setSelectedExemplaireId(undefined);
              setInitialExemplaireData(undefined);
            }
          }}
          exemplaireId={selectedExemplaireId}
          multipleprintId={multipleprintId}
          initialData={initialExemplaireData}
        />
      )}
    </div>
    </CheckboxGroupMask>
  );
};

export default ExemplaireDashboard;
